import React, { useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useParams } from "react-router-dom";
import ReactGA from "react-ga4";
import { backendURL, useApiFetch } from "../../utils/api";
import { Competition } from "../../types/Competition";
import { ShootingSession } from "../../types/ShootingSession";
import { getShootingEventConfig } from "../../configs/utils/ShootingEventConfigFactory";
import EventRankingTable from "../../components/EventRankingTable";
import EventRankingSummary from "../../components/EventRankingSummary";
import { useNotifications } from "../../contexts/NotificationContext";
import "../../components/EventRankingTable.css";
import { useCookieConsent } from "../../contexts/CookieConsentContext";

function EventRankingReport() {
    const { competitionId, shootingEventId } = useParams<{ competitionId: string; shootingEventId: string }>();
    const apiFetch = useApiFetch();
    const [competition, setCompetition] = useState<Competition | null>(null);
    const [sessions, setSessions] = useState<ShootingSession[]>([]);
    const { info } = useNotifications();
    const HINT_KEY = "session-row-click-hint-seen";

    const isConsentGiven: boolean = useCookieConsent().isConsentGiven;
    const { analyticsConsent } = useCookieConsent();
    
    useEffect(() => {
        if (!competitionId) return;

        const fetchCompetition = async () => {
            try {
                const data = await apiFetch(`${backendURL}/competitions/${competitionId}`, {
                    method: "GET",
                    includeAuth: true,
                });
                setCompetition(data);
            } catch (err) {
                console.error("Error fetching competition:", err);
            }
        };

        fetchCompetition();
    }, [competitionId]);

    useEffect(() => {
        if (!competitionId || !shootingEventId) return;

        const fetchSessions = async () => {
            try {
                const params = new URLSearchParams({ competitionId, shootingEventId });
                const data = await apiFetch(`${backendURL}/shooting-sessions/competition-sessions?${params}`, {
                    method: "GET",
                    includeAuth: true,
                });
                setSessions(data);
            } catch (err) {
                console.error("Error fetching shooting sessions:", err);
            }
        };

        fetchSessions();
    }, [competitionId, shootingEventId]);

    useEffect(() => {
        if (analyticsConsent === "accepted" && competition && sessions.length > 0 && shootingEventId) {
            const eventName = (() => { try { return getShootingEventConfig(shootingEventId).shootingEventName; } catch { return shootingEventId; } })();
            ReactGA.event({
                category: "User",
                action: "Event ranking report viewed",
                label: `${competition.name} – ${eventName}`
            });
        }
    }, [sessions, competition, analyticsConsent]);

    useEffect(() => {
            if (!(sessions.length > 0)) return;
    
            if (!localStorage.getItem(HINT_KEY) && isConsentGiven) {
                info("Kliknij wiersz tabeli, aby otworzyć szczegóły sesji - rozkład trafień i statystyki", 0);
                localStorage.setItem(HINT_KEY, "1");
            }
        }, [sessions, isConsentGiven]);

    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        return date.toLocaleDateString('pl-PL');
    };

    const shootingEventName = shootingEventId
        ? (() => { try { return getShootingEventConfig(shootingEventId).shootingEventName; } catch {
            return "Nieprawidłowe ID konkurencji w adresie URL: " + shootingEventId;
        } })()
        : "";

    return (
        <Container>
            <Row>
                <Col xl={8} md={10} xs={12} className="mx-auto">
                    <div className="py-3">
                        {competition ? (
                            <>
                                <h1 className="font-weight-light text-center mb-1">
                                    {competition.name} {formatDate(competition.date)}
                                </h1>
                                <h3 className="font-weight-light text-center mb-3">
                                    {shootingEventName}
                                </h3>
                                <EventRankingTable
                                    sessions={sessions}
                                    shootingEventId={shootingEventId!}
                                    competitionName={competition.name}
                                    competitionDate={competition.date}
                                />
                                <EventRankingSummary sessions={sessions} competitionName={competition.name} competitionDate={competition.date} />
                            </>
                        ) : (
                            <p className="text-center mb-4">Brak danych</p>
                        )}
                    </div>
                </Col>
            </Row>
        </Container>
    );
}

export default EventRankingReport;
